import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { IAnnotationType } from './models/annotationType';
import { IAnnotationTypeOptions } from './models/annotationTypeOptions';

interface IHiddenAnnotationType {
  projectId: string;
  annotationTypes: string[];
}

export interface IAnnotationTypeStore {
  readonly activeAnnotationTypeId: string | undefined;
  readonly annotationTypes: IAnnotationType[];
  readonly hiddenAnnotationTypes: IHiddenAnnotationType[];
  readonly annotationTypesOptions: IAnnotationTypeOptions[];
  readonly annotationTypeKeyHandlers: Map<string, () => void>;

  setAnnotationTypes(annotationTypes: IAnnotationType[]): void;
  setActiveAnnotationTypeId(activeAnnotationTypeId: string | undefined): void;
  setHiddenAnnotationTypes(hiddenAnnotationTypes: IHiddenAnnotationType[]): void;
  setAnnotationTypeOptions(annotationTypesOptions?: IAnnotationTypeOptions[]): void;
  setAnnotationTypeKeyMap(annotationTypeKeyHandlers: Map<string, () => void>): void;
  clean(): void;
}

export namespace IAnnotationTypeStore {
  export const $: interfaces.ServiceIdentifier<IAnnotationTypeStore> = Symbol('ANNOTATION_TYPE_STORE');
}

@injectable()
export class AnnotationTypeStore implements IAnnotationTypeStore {
  annotationTypes: IObservableArray<IAnnotationType> = observable.array([]);

  hiddenAnnotationTypes: IHiddenAnnotationType[] = [];

  activeAnnotationTypeId: string | undefined = undefined;

  annotationTypesOptions: IAnnotationTypeOptions[] = [];

  annotationTypeKeyHandlers: Map<string, () => void> = new Map<string, () => void>();

  constructor() {
    makeObservable(this, {
      annotationTypes: observable,
      hiddenAnnotationTypes: observable,
      activeAnnotationTypeId: observable,
      annotationTypesOptions: observable,
      annotationTypeKeyHandlers: observable,
      setAnnotationTypes: action,
      setHiddenAnnotationTypes: action,
      setActiveAnnotationTypeId: action,
      setAnnotationTypeOptions: action,
      setAnnotationTypeKeyMap: action,
      clean: action,
    });
  }

  setAnnotationTypes(annotationTypes: IAnnotationType[]): void {
    this.annotationTypes.replace(annotationTypes);
  }

  setHiddenAnnotationTypes(hiddenAnnotationTypes: IHiddenAnnotationType[]): void {
    this.hiddenAnnotationTypes = hiddenAnnotationTypes;
  }

  setActiveAnnotationTypeId(activeAnnotationTypeId?: string): void {
    this.activeAnnotationTypeId = activeAnnotationTypeId;
  }

  setAnnotationTypeOptions(annotationTypesOptions: IAnnotationTypeOptions[]): void {
    this.annotationTypesOptions = annotationTypesOptions;
  }

  setAnnotationTypeKeyMap(annotationTypeKeyHandlers: Map<string, () => void>): void {
    this.annotationTypeKeyHandlers = annotationTypeKeyHandlers;
  }

  clean(): void {
    this.annotationTypes.replace([]);
    this.activeAnnotationTypeId = undefined;
  }
}
